export type GrmField = 'price' | 'rentPerUnit' | 'units' | 'otherIncome';

export type GrmValues = Record<GrmField, string>;

export type GrmErrors = Partial<Record<GrmField, string>>;

export type GrmResult = {
  grm: number;
  monthlyGrossIncome: number;
  annualGrossIncome: number;
  grossYield: number;
  monthlyIncomeToPrice: number;
  meetsOnePercentRule: boolean;
};

export type GrmCalculation = { ok: true; result: GrmResult } | { ok: false; errors: GrmErrors };

export type GrmComparison = { lower: 'a' | 'b' | null; difference: number };

const roundTo2 = (value: number) => Math.round(value * 100) / 100;

export const parseWholeNumber = (value: string): number | null => {
  const digits = value.split('.')[0].replace(/\D/g, '');
  return digits === '' ? null : Number(digits);
};

export const calculateGrm = (values: GrmValues): GrmCalculation => {
  const price = parseWholeNumber(values.price);
  const rentPerUnit = parseWholeNumber(values.rentPerUnit);
  const units = parseWholeNumber(values.units);
  const otherIncome = parseWholeNumber(values.otherIncome) ?? 0;

  const errors: GrmErrors = {};

  if (!price) {
    errors.price = 'Enter a price greater than $0';
  }
  if (!units) {
    errors.units = 'Enter at least 1 unit';
  }
  if (rentPerUnit === null) {
    errors.rentPerUnit = 'Enter a monthly rent';
  } else if (units && rentPerUnit * units + otherIncome === 0) {
    errors.rentPerUnit = 'Enter rent or other income greater than $0';
  }

  if (!price || !units || rentPerUnit === null || Object.keys(errors).length > 0) {
    return { ok: false, errors };
  }

  const monthlyGrossIncome = rentPerUnit * units + otherIncome;
  const annualGrossIncome = monthlyGrossIncome * 12;
  const monthlyIncomeToPrice = monthlyGrossIncome / price;

  return {
    ok: true,
    result: {
      grm: price / annualGrossIncome,
      monthlyGrossIncome,
      annualGrossIncome,
      grossYield: annualGrossIncome / price,
      monthlyIncomeToPrice,
      meetsOnePercentRule: monthlyIncomeToPrice >= 0.01,
    },
  };
};

export const compareGrm = (a: GrmResult, b: GrmResult): GrmComparison => {
  const difference = roundTo2(Math.abs(roundTo2(a.grm) - roundTo2(b.grm)));

  if (difference === 0) {
    return { lower: null, difference };
  }

  return { lower: a.grm < b.grm ? 'a' : 'b', difference };
};

export const formatWholeNumber = (value: string) => {
  const parsed = parseWholeNumber(value);
  return parsed === null ? '' : parsed.toLocaleString('en-US');
};

export const formatCurrency = (value: number) =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

export const formatPercent = (ratio: number) => `${(ratio * 100).toFixed(2)}%`;

export const formatPercentRoundedDown = (ratio: number) => `${(Math.floor(ratio * 10000 + 1e-9) / 100).toFixed(2)}%`;

export const formatGrm = (grm: number) => grm.toFixed(2);
